export const DEFAULT_MAX_ROUTE_POINTS = 20;
export const DEFAULT_MAX_EXTRA_MINUTES = 180;
export const DEFAULT_EXTRA_MINUTES = 30;
/** Riding preferences use a 1 (neutral) to 10 (strongest) scale. */
export const PREFERENCE_MIN = 1;
export const PREFERENCE_MAX = 10;
export const DEFAULT_PREFERENCE = 5;
export const DEFAULT_RETURN_RADIUS_KM = 10;
export const MAX_RETURN_RADIUS_KM = 100;
/** Signed return preference: -10 same roads, 0 neutral, +10 different roads. */
export const RETURN_PREFERENCE_LIMIT = 10;
export interface ReturnRouting {
  preference: number;
  nearStartKm: number;
}
export const DEFAULT_RETURN_ROUTING: ReturnRouting = { preference: 0, nearStartKm: DEFAULT_RETURN_RADIUS_KM };

export interface ReturnRouteStatus {
  requestedPreference: number;
  applied: boolean;
  eligible: boolean;
  appliedPreference: number;
  turnaroundPointId?: string;
  message: string;
}

export interface PlannerConfig {
  maxRoutePoints: number;
  maxExtraMinutes: number;
  coverage: string;
}

export type LocationSource = 'search' | 'pin';

export interface PlannedPoint {
  id: string;
  label: string;
  lat: number;
  lng: number;
  source?: LocationSource;
}

export interface RidingPreferences {
  winding: number;
  motorways: number;
  trafficLights: number;
}

export interface PlanRequest {
  points: PlannedPoint[];
  preferences: RidingPreferences;
  extraMinutes: number;
  returnRouting?: ReturnRouting;
}

export interface RouteGeometry {
  coordinates: Array<[number, number]>;
}

/** How the route moved through the road nodes it passed, counted per graph edge. */
export interface JunctionStats {
  /** Nodes passed with 3, 4 or 5+ roads meeting at them (all roads in the graph, not only drivable ones). */
  threeWay: number;
  fourWay: number;
  fivePlus: number;
  /** Of those, the nodes that offered at least two drivable exits. */
  withChoice: number;
  /** How the route left each of those nodes. */
  straight: number;
  left: number;
  right: number;
  uTurn: number;
}

/** What GraphHopper's per-edge path details say about a route. Every count is per graph edge. */
export interface RouteStats {
  /** Graph edges travelled: the road pieces between two routing nodes. */
  edges: number;
  /** Sum of the edges' lengths, in metres. */
  meters: number;
  /** Metres by road class, surface and road environment, keyed by GraphHopper's value names. */
  roadClass: Record<string, number>;
  surface: Record<string, number>;
  environment: Record<string, number>;
  /** Metres by the curvature bands the winding preference scores (see `models.ts`). */
  curvature: { straight: number; gentle: number; winding: number };
  /** Edges by what the rider meets on arriving at the edge's end (`arrival_control`). */
  controls: Record<string, number>;
  junctions: JunctionStats;
  /** Times the route moved from one OSM way to another, within each leg. */
  wayChanges: number;
}

/** The traffic-signal classes of `arrival_control`; see ARCHITECTURE "Traffic controls". */
export type SignalControl = 'SIGNAL_THREE' | 'SIGNAL_FOUR' | 'SIGNAL_LEFT' | 'SIGNAL_OTHER';

/** A mapped traffic signal the route arrives at: `index` is its point in the route's line. */
export interface RouteSignal {
  index: number;
  control: SignalControl;
}

/** [first point, last point, km/h] of a stretch with one mapped speed limit; null where none is mapped. */
export type SpeedLimitStretch = [number, number, number | null];

/** What the planner map draws along the route, indexed into its line. */
export interface RouteFeatures {
  signals: RouteSignal[];
  speedLimits: SpeedLimitStretch[];
}

export interface RouteLeg {
  fromIndex: number;
  toIndex: number;
  timeSeconds: number;
  distanceMeters: number;
  usesFerry: boolean;
  warnings: string[];
  wayIds: number[];
  stats?: RouteStats;
}

export interface RouteResult {
  geometry: RouteGeometry;
  distanceMeters: number;
  timeSeconds: number;
  usesFerry: boolean;
  warnings?: string[];
  features?: RouteFeatures;
  /** Internal engine metadata, not sent as part of the response route. */
  legs?: RouteLeg[];
  snappedPoints?: Array<[number, number]>;
  stats?: RouteStats;
  /** Nodes the engine visited finding this route, summed over its legs. */
  visitedNodes?: number;
}

/** One routing search made while planning. */
export interface PlanSearch {
  kind: 'baseline' | 'candidate' | 'return';
  preference?: number;
  multiplier?: number;
  preferences?: RidingPreferences;
  /** Distinct outward road IDs sent to the engine (return searches only). */
  roadIds?: number;
  ms: number;
  visitedNodes?: number;
  seconds?: number;
  meters?: number;
  fits?: boolean;
  error?: string;
}

/** Engine-level detail about a plan, for the route internals view. */
export interface PlanDebug {
  /** Server time for the whole plan, including every engine search. */
  durationMs: number;
  searches: PlanSearch[];
  baseline: { seconds: number; meters: number; visitedNodes?: number; stats?: RouteStats };
  route: {
    /** Points in the drawn line. */
    points: number;
    distinctWays?: number;
    stats?: RouteStats;
    legs?: Array<{ from: string; to: string; meters: number; seconds: number; ways: number; edges?: number }>;
  };
  /** How far the engine moved each requested place to reach a drivable road. */
  stops: Array<{ id: string; label: string; snapMeters?: number }>;
}

export interface PlanResponse {
  /** Identifies this plan in the API logs; equal to the response's x-request-id. */
  planId: string;
  route: RouteResult;
  baseline: Pick<RouteResult, 'distanceMeters' | 'timeSeconds' | 'usesFerry'>;
  allowanceSeconds: number;
  extraSeconds: number;
  /** Preferences actually applied to the selected route, on the request scale. */
  appliedPreferences: RidingPreferences;
  relaxed: boolean;
  message: string;
  warnings: string[];
  coverage: string;
  plannedPointIds: string[];
  returnRoute?: ReturnRouteStatus;
  /** What a ride trip needs to rebuild exactly this route for the ride app; absent if the engine reported no road data version. */
  ride?: RideIdentity;
  debug: PlanDebug;
}

/** Identifies a planned route: the road data it was planned on and a fingerprint of its geometry. */
export interface RideIdentity {
  graphVersion: string;
  routeFingerprint: string;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}
